import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { slides } from '../data/homepage';
import { HeroAtna } from './HeroAtna';
const toneClass: Record<string, string> = {
  coral: 'bg-ville-coral text-white',
  blue: 'bg-ville-blue text-white',
  navy: 'bg-ville-navy text-white'
};
export function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((next: number) => {
    setIndex((next + slides.length) % slides.length);
  }, []);
  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => setIndex(i => (i + 1) % slides.length), 7000);
    return () => window.clearInterval(id);
  }, [paused]);
  const slide = slides[index];
  return <section aria-label="À la une" aria-roledescription="carrousel" className="relative w-full overflow-hidden bg-[linear-gradient(to_bottom,#e9eef7_0%,#e9eef7_55%,#f4f7fc_80%,#ffffff_100%)] pb-20 pt-8 lg:pb-20 lg:pt-10" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-1657" data-mp="35xmrss31210q">
      <div className="mx-auto grid max-w-[1400px] items-center gap-8 px-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,38%)] lg:gap-12 lg:px-10" data-id="element-1658" data-mp="3khv9fqjzjacn">
        <div className="min-w-0" data-id="element-1659" data-mp="3pgettmixke2w">
          <AnimatePresence mode="wait" data-id="element-1660" data-mp="2sr78aeiba4yz">
            <motion.div key={slide.id} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-1661" data-mp="2ss8jcizburgp">
              <span className={`inline-block rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider ${toneClass[slide.tone]}`} data-id="element-1662" data-mp="36ivgi6pfw9ao">
                {slide.eyebrow}
              </span>
              <h1 className="mt-4 text-3xl font-extrabold leading-[1.08] tracking-tight text-ville-navy sm:text-4xl lg:text-[2.75rem]" data-id="element-1663" data-mp="2e0q7mxdi3j35">
                {slide.title}
              </h1>
              <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-ville-navy/70 lg:text-base" data-id="element-1664" data-mp="3ccssp9j0ou4r">
                {slide.description}
              </p>
              <p className="mt-3 text-base font-bold text-ville-deep" data-id="element-1665" data-mp="3m3nfcf06dvqq">{slide.date}</p>
              <a href="#accueil" className="mt-5 inline-flex items-center gap-2 rounded-full bg-ville-blue px-6 py-3 text-[15px] font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-navy focus-visible:ring-offset-2 focus-visible:ring-offset-ville-mist" data-id="element-1666" data-mp="3m5rrfk88a123">
                {slide.cta}
                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1667" data-mp="moa33buevvfl" />
              </a>
            </motion.div>
          </AnimatePresence>

          <div className="mt-7 hidden max-w-xl lg:block" data-id="element-1668" data-mp="hoxd4i88u88s">
            <HeroAtna data-id="element-1669" data-mp="q5y1xqduvrxm" />
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-[420px]" data-id="element-1670" data-mp="q5656c8ivo5r">
          <div className="relative aspect-square w-full overflow-hidden rounded-full" data-id="element-1671" data-mp="u943ikq0wuxr">
            <AnimatePresence initial={false} data-id="element-1672" data-mp="g933f7hl1rjc">
              <motion.img key={slide.id} src={slide.image} alt="" initial={{
              opacity: 0,
              scale: 1.02
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.35,
              ease: [0.23, 1, 0.32, 1]
            }} className="absolute inset-0 h-full w-full rounded-full object-cover" data-id="element-1673" data-mp="ga4idn1pyy6c" />
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-7 flex max-w-[1400px] items-center justify-between px-5 lg:mt-8 lg:px-10" data-id="element-1674" data-mp="1445sngkatx0c">
        <div className="flex items-center gap-3" data-id="element-1675" data-mp="1ru04busvtfj0">
          {slides.map((s, i) => <button key={s.id} type="button" onClick={() => go(i)} aria-label={`Aller à la diapositive ${i + 1} : ${s.title}`} aria-current={i === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-ville ${i === index ? 'w-10 bg-ville-blue' : 'w-2.5 bg-ville-navy/20 hover:bg-ville-navy/40'}`} data-id="element-1676" data-mp="1wnuot0gb9yj5" data-mpi={i} />)}
        </div>
        <div className="flex items-center gap-2" data-id="element-1677" data-mp="rnwmhaxjro36">
          <button type="button" onClick={() => go(index - 1)} aria-label="Diapositive précédente" className="grid h-11 w-11 place-items-center rounded-full border border-ville-navy/20 text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue" data-id="element-1678" data-mp="mp387gafrt1y">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-1679" data-mp="15o7cycuy30nt" />
          </button>
          <button type="button" onClick={() => go(index + 1)} aria-label="Diapositive suivante" className="grid h-11 w-11 place-items-center rounded-full border border-ville-navy/20 text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue" data-id="element-1680" data-mp="1d05a72u3nay7">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1681" data-mp="1vz9i289wmyoa" />
          </button>
        </div>
      </div>
    </section>;
}